import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { MarketingPage } from '@/components/marketing/marketing-page'
import { JsonLd } from '@/components/seo/json-ld'
import { BLOG_POSTS } from '@/content/blog/posts'
import { blogListBreadcrumb } from '@/content/blog/blog-schema'

const PATH = '/blog'

/** Formats a fixed ISO date for the active locale; falls back to the ISO string. */
function formatDate(iso: string, language: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

/** The /blog index: a card grid of every article, newest copy first. */
export function BlogIndexPage() {
  const { t, i18n } = useTranslation()
  const blogLabel = t('blog.index.crumb')

  return (
    <MarketingPage
      seoTitle={t('blog.index.seo.title')}
      seoDescription={t('blog.index.seo.description')}
      path={PATH}
      eyebrow={t('blog.index.eyebrow')}
      heading={t('blog.index.h1')}
      lead={t('blog.index.lead')}
    >
      <JsonLd data={blogListBreadcrumb(blogLabel)} />

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="glass-card glass-hover group flex flex-col rounded-2xl p-6 no-underline"
            >
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="mt-4 text-xl font-semibold leading-snug text-foreground">
                {t(`blog.${post.i18nKey}.h1`)}
              </h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                {t(`blog.${post.i18nKey}.seo.description`)}
              </p>
              <div className="mt-5 flex items-center justify-between">
                <time dateTime={post.date} className="text-xs text-muted-foreground">
                  {formatDate(post.date, i18n.language)}
                </time>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {t('blog.index.readArticle')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </MarketingPage>
  )
}
